import { Dayjs } from '@seolens/date-manager';
import { Form } from 'antd';
import { FC, memo, useCallback, useState } from 'react';

import { AppMessage } from '@/common/components';
import { BaseQueryParams, FormFieldsErrors } from '@/common/interfaces';
import { extractFormErrors, getTodayInTimeZone, messageOnError } from '@/common/utils';

import {
    DEFAULT_SEARCH_CONSOLE_ANALYTICS_PERIOD,
    SEARCH_CONSOLE_ANALYTICS_PERIOD_START_DATE,
    SEARCH_CONSOLE_TIME_ZONE,
    SearchConsoleAnalyticsFormFields,
    SearchConsoleAnalyticsFormInputs,
    SearchConsoleAnalyticsRequestData,
    useGetSearchConsoleAnalyticsMutation
} from '@/modules/Domains';

import { SearchConsoleAnalyticsFormView } from './SearchConsoleAnalyticsFormView';
import { searchConsoleAnalyticsFormValidationSchema } from './validation';

type Props = {
  siteId: string;
  domainId: string;
  handleCollectAnalytics: (data: (BaseQueryParams<typeof useGetSearchConsoleAnalyticsMutation>['data']) | null) => void;
};

export const SearchConsoleAnalyticsFormContainer: FC<Props> = memo(({
  siteId,
  domainId,
  handleCollectAnalytics
}) => {
  const appMessageApi = AppMessage.useControls();
  const [form] = Form.useForm<SearchConsoleAnalyticsFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof SearchConsoleAnalyticsFormInputs>>(null);

  const {
    mutate: getAnalyticsMutation,
    isLoading: isGetAnalyticsMutationLoading,
  } = useGetSearchConsoleAnalyticsMutation(siteId, domainId)({
    onSuccess: (d) => {
      appMessageApi.success('Запит виконано успішно!');

      handleCollectAnalytics(d);
    },
    onError: (err) => {
      setFormErrors(extractFormErrors<typeof SearchConsoleAnalyticsFormInputs>(err));
      messageOnError(err, appMessageApi);

      handleCollectAnalytics(null);
    },
    onMutate: () => {
      handleCollectAnalytics(null);
    }
  });

  const today = getTodayInTimeZone(SEARCH_CONSOLE_TIME_ZONE);

  const initialValues: SearchConsoleAnalyticsFormFields = {
    dataState: 'final',
    startDate: SEARCH_CONSOLE_ANALYTICS_PERIOD_START_DATE[DEFAULT_SEARCH_CONSOLE_ANALYTICS_PERIOD](today),
    endDate: today,
  };

  const handleFinish = useCallback((values: SearchConsoleAnalyticsFormFields): void => {
    setFormErrors(null);

    getAnalyticsMutation({
      dataState: values.dataState as SearchConsoleAnalyticsRequestData['dataState'],
      startDate: (values.startDate as Dayjs).format('YYYY-MM-DD'),
      endDate: (values.endDate as Dayjs).format('YYYY-MM-DD')
    });
  }, []);

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
  }, []);

  const handleToggleDataState = (checked: boolean) => {
    form.setFieldValue([SearchConsoleAnalyticsFormInputs.DataState], checked ? 'all' : 'final');
  };

  return (
    <>
      <SearchConsoleAnalyticsFormView
        formName="search-console-analytics-form"
        formType="create"
        form={ form }
        formErrors={ formErrors }
        initialValues={ initialValues }
        isFormLoading={ isGetAnalyticsMutationLoading }
        validationSchema={ searchConsoleAnalyticsFormValidationSchema }
        handleBack={ () => {} }
        handleToggleDataState={ handleToggleDataState }
        handleFinish={ handleFinish }
        handleFinishFailed={ handleFinishFailed }
      />
    </>
  );
});
